import { Component, OnInit, Renderer2 } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ERTMedicalRunSheetService } from "../../services/emergency-response/medical-runsheet.service";
import {
  RespiratoryType,
  PatientType,
  ConsciousnessLevel,
  SkinCondition,
  ERTGender,
} from "../../interfaces/emergencyresponse/medical-runsheet-enum";
import { ERTMedicalRunSheet } from "../../interfaces/emergencyresponse/medical-runsheet";
import { ERTEmergencyResponse } from "../../interfaces/emergencyresponse/emergencyresponse";
import { ERTEmergencyResponseService } from "../../services/emergency-response/emergency-response.service";
import { ERTUser } from "../../interfaces/ert-user/ert-user";
import { ERTUserService } from "../../services/ert-user/ert-user.service";
import { LoggedUserService } from "../../services/common/logged-user.service";

@Component({
  selector: "app-medical-runsheet-view",
  templateUrl: "./medical-runsheet-view.component.html",
  styles: [
    `
      ::ng-deep mat-expansion-panel-header .mat-expansion-indicator {
        display: none;
      }

      ::ng-deep mat-icon {
        color: white !important;
      }
    `,
  ],
})
export class MedicalRunsheetViewComponent implements OnInit {
  medicalRunsheetId: string;
  ertEmergencyResponseId: string;
  incidentDateAndTime: string;
  location: string;

  emergencyResponse: ERTEmergencyResponse;
  ertMedicalRunSheet: ERTMedicalRunSheet = {} as ERTMedicalRunSheet;
  ertMedicalRunSheetViewForm: FormGroup;
  formErrors: any[] = [];
  icCommander: ERTUser;

  patientGenders = ERTGender;
  respiratoryTypes = RespiratoryType;
  patientTypes = PatientType;
  consciousnessLevels = ConsciousnessLevel;
  skinConditions = SkinCondition;

  runInfoPanelOpenState = true;
  patientInfoPanelOpenState = true;

  facilityId: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ertUserService: ERTUserService,
    private ertEmergencyResponseService: ERTEmergencyResponseService,
    private ertMedicalRunSheetService: ERTMedicalRunSheetService,
    private fb: FormBuilder,
    private renderer: Renderer2,
    private loggedUserService: LoggedUserService
  ) {
    this.ertMedicalRunSheetViewForm = this.fb.group({
      incidentDateTime: [""],
      medicalTeam: [""],
      locationOfIncident: [""],
      medicalSignOff: [""],
      hospital: [""],
      arrivalDateTime: [""],
      readiedForNextRun: [""],
      fuelOk: [""],
      comments: [""],
      patientName: [""],
      patientAge: [""],
      patientGender: [""],
      patientEmployer: [""],
      chiefComplaint: [""],
      history: [""],
      assessment: [""],
      treatment: [""],
      equipment: [""],
      respiration: [""],
      dateTimeOfRecord: [""],
      type: [""],
      pulseRate: [""],
      systolicRate: [""],
      diastolicRate: [""],
      levelsOfConsciousness: [""],
      diastolicPupils: [""],
      skin: [""],
    });
  }

  ngOnInit() {
     this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.facilityId = Number(user.facilityId) || 0;
      this.route.queryParamMap.subscribe((params) => {
        this.medicalRunsheetId = params.get("medicalRunSheetId");
        this.loadData(this.medicalRunsheetId);
      });
     });
  }

  loadData(medicalRunsheetId: string) {
    this.ertMedicalRunSheetService
      .getERTMedicalRunSheetById(Number(medicalRunsheetId), this.facilityId)
      .subscribe({
        next: (data) => {
          if(data.emergencyResponse == null || data.emergencyResponse.facilityId !== this.facilityId) {
            this.router.navigate(["/no-access"]);
            return;
          }
          this.ertEmergencyResponseId = data.emergencyResponseId.toString();
          this.incidentDateAndTime = this.formatDate(
            data.dateAndTimeOfIncident?.slice(0, 16)
          );
          this.location = data.locationOfIncident;

          this.ertMedicalRunSheetViewForm.patchValue({
            incidentDateTime: data.dateAndTimeOfIncident?.slice(0, 16),
            locationOfIncident: data.locationOfIncident,
            medicalTeam: data.medicalTeam,
            medicalSignOff: data.medicalSignOff,
            hospital: data.patientHospital,
            arrivalDateTime: data.dateTimePatientArrival?.slice(0, 16),
            readiedForNextRun: data.isReadiedForNextRun,
            fuelOk: data.isFuelOk,
            comments: data.comments,
            patientName: data.patientName,
            patientAge: data.patientAge,
            patientEmployer: data.patientEmployer,
            chiefComplaint: data.cheifComplaint,
            history: data.history,
            assessment: data.assessment,
            treatment: data.treatment,
            equipment: data.equipment,
            respiration: data.respiratory,
            dateTimeOfRecord: data.dateTimeOfRecord?.slice(0, 16),
            type: data.type,
            pulseRate: data.pulseRate,
            systolicRate: data.systolicRate,
            diastolicRate: data.diastolicRate,
            levelsOfConsciousness: data.levelOfConsciousness,
            diastolicPupils: data.diastolicPupils,
            skin: data.skin,
            patientGender: data.patientGender,
          });

          this.ertEmergencyResponseService
            .getERTEmergencyResponseById(Number(this.ertEmergencyResponseId), this.facilityId)
            .subscribe({
              next: (data) => {
                this.emergencyResponse = data;

                this.ertUserService
                  .getERTUserById(this.emergencyResponse.incidentCommanderId, this.facilityId)
                  .subscribe((data) => {
                    this.icCommander = data;
                  });
              },
              error: (error) => {
                console.error("mrs-view - error: ", error);
              },
            });
        },
        error: (error) => {},
      });
  }

  formatDate(input: string): string {
    const date = new Date(input);

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    let hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = Number(hours) >= 12 ? "PM" : "AM";
    hours = (Number(hours) % 12).toString().padStart(2, "0");
    hours = Number(hours) ? hours : "12";
    const strTime = `${hours}:${minutes} ${ampm}`;

    return `${month}/${day}/${year} ${strTime}`;
  }

  submitForm() {
    if (!this.ertMedicalRunSheet) {
      this.ertMedicalRunSheet = {} as ERTMedicalRunSheet;
    }
    this.ertMedicalRunSheet.id = Number(this.medicalRunsheetId);
    this.ertMedicalRunSheet.emergencyResponseId = Number(
      this.ertEmergencyResponseId
    );
    //this.ertMedicalRunSheet.emergencyResponse = this.emergencyResponse;
    this.ertMedicalRunSheet.icUserId =
      this.emergencyResponse.incidentCommanderId;
    //this.ertMedicalRunSheet.icUser = this.icCommander;
    this.ertMedicalRunSheet.dateAndTimeOfIncident =
      this.ertMedicalRunSheetViewForm.get("incidentDateTime").value;
    this.ertMedicalRunSheet.locationOfIncident =
      this.ertMedicalRunSheetViewForm.get("locationOfIncident").value;
    this.ertMedicalRunSheet.medicalTeam =
      this.ertMedicalRunSheetViewForm.get("medicalTeam").value;
    this.ertMedicalRunSheet.patientName =
      this.ertMedicalRunSheetViewForm.get("patientName").value;
    this.ertMedicalRunSheet.patientAge =
      this.ertMedicalRunSheetViewForm.get("patientAge").value;

    const patientGenderValue =
      this.ertMedicalRunSheetViewForm.get("patientGender").value;
    this.ertMedicalRunSheet.patientGender =
      patientGenderValue !== null ? Number(patientGenderValue) : null;

    this.ertMedicalRunSheet.patientHospital =
      this.ertMedicalRunSheetViewForm.get("hospital").value;
    this.ertMedicalRunSheet.patientEmployer =
      this.ertMedicalRunSheetViewForm.get("patientEmployer").value;
    this.ertMedicalRunSheet.dateTimePatientArrival =
      this.ertMedicalRunSheetViewForm.get("arrivalDateTime").value;
    this.ertMedicalRunSheet.comments =
      this.ertMedicalRunSheetViewForm.get("comments").value;
    this.ertMedicalRunSheet.isReadiedForNextRun =
      this.ertMedicalRunSheetViewForm.get("readiedForNextRun").value;
    this.ertMedicalRunSheet.isFuelOk =
      this.ertMedicalRunSheetViewForm.get("fuelOk").value;
    this.ertMedicalRunSheet.cheifComplaint =
      this.ertMedicalRunSheetViewForm.get("chiefComplaint").value;
    this.ertMedicalRunSheet.history =
      this.ertMedicalRunSheetViewForm.get("history").value;
    this.ertMedicalRunSheet.assessment =
      this.ertMedicalRunSheetViewForm.get("assessment").value;
    this.ertMedicalRunSheet.treatment =
      this.ertMedicalRunSheetViewForm.get("treatment").value;
    this.ertMedicalRunSheet.equipment =
      this.ertMedicalRunSheetViewForm.get("equipment").value;
      this.ertMedicalRunSheet.medicalSignOff =
      this.ertMedicalRunSheetViewForm.get("medicalSignOff").value;
    const respiratoryValue =
      this.ertMedicalRunSheetViewForm.get("respiration").value;
    this.ertMedicalRunSheet.respiratory =
      respiratoryValue !== null ? Number(respiratoryValue) : null;

    const typeValue = this.ertMedicalRunSheetViewForm.get("type").value;
    this.ertMedicalRunSheet.type =
      typeValue !== null ? Number(typeValue) : null;

    this.ertMedicalRunSheet.systolicRate =
      this.ertMedicalRunSheetViewForm.get("systolicRate").value;

    const levelsOfConsciousnessValue = this.ertMedicalRunSheetViewForm.get(
      "levelsOfConsciousness"
    ).value;
    this.ertMedicalRunSheet.levelOfConsciousness =
      levelsOfConsciousnessValue !== null
        ? Number(levelsOfConsciousnessValue)
        : null;

    const skinValue = this.ertMedicalRunSheetViewForm.get("skin").value;
    this.ertMedicalRunSheet.skin =
      skinValue !== null ? Number(skinValue) : null;

    this.ertMedicalRunSheet.dateTimeOfRecord =
      this.ertMedicalRunSheetViewForm.get("dateTimeOfRecord").value;
    this.ertMedicalRunSheet.pulseRate =
      this.ertMedicalRunSheetViewForm.get("pulseRate").value;
    this.ertMedicalRunSheet.diastolicRate =
      this.ertMedicalRunSheetViewForm.get("diastolicRate").value;
    this.ertMedicalRunSheet.diastolicPupils =
      this.ertMedicalRunSheetViewForm.get("diastolicPupils").value;

    this.ertMedicalRunSheetService
      .updateERTMedicalRunSheet(this.ertMedicalRunSheet, this.facilityId)
      .subscribe({
        next: (data) => {
          this.router.navigate(
            ["/ert-emergency-response/emergency-response-view"],
            {
              queryParams: {
                emergencyResponseId: this.ertEmergencyResponseId,
              },
            }
          );
        },
        error: (error) => {
          this.formErrors.push("Error updating emergency response");
        },
      });
  }

  navigateToPrint(id: string): void {
    this.ertMedicalRunSheetService.printMedicalRunSheet(id, this.facilityId);
  }

  getEnumKeys(enumObj: any): string[] {
    return Object.keys(enumObj).filter((key) => isNaN(Number(key)));
  }
}
